import { useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Loader2, CalendarCheck, CheckCircle2, MapPin } from "lucide-react";

const GOLD = "#C9A24B";
const emptyBooking = { customer_name: "", phone: "", customer_email: "", guests: 2, date: "", time: "", notes: "" };

const T = {
  it: {
    eyebrow: "Lounge Garden Bar", title: "Prenota un tavolo",
    intro: "Compila i tuoi dati: riceverai la conferma via email.",
    name: "Nome e cognome", phone: "Telefono", email: "Email (per ricevere la conferma)", emailPh: "tua@email.it",
    date: "Data", guests: "Ospiti", slot: "Orario disponibile", noSlot: "Nessun orario configurato per questa data.",
    full: "pieno", seats: "posti", notes: "Note (opzionale)", notesPh: "Allergie, seggiolone, occasione speciale…",
    confirm: "Conferma prenotazione", okTitle: "Prenotazione confermata!", okBody: "Ti aspettiamo da Dogana 2.0. A presto!",
    another: "Fai un'altra prenotazione", menu: "Guarda il menù",
    errName: "Inserisci nome, data e orario", err: "Si è verificato un errore, riprova.",
  },
  en: {
    eyebrow: "Lounge Garden Bar", title: "Book a table",
    intro: "Fill in your details: you'll receive the confirmation by email.",
    name: "Full name", phone: "Phone", email: "Email (to receive the confirmation)", emailPh: "you@email.com",
    date: "Date", guests: "Guests", slot: "Available time", noSlot: "No time slots configured for this date.",
    full: "full", seats: "seats", notes: "Notes (optional)", notesPh: "Allergies, high chair, special occasion…",
    confirm: "Confirm booking", okTitle: "Booking confirmed!", okBody: "See you soon at Dogana 2.0!",
    another: "Make another booking", menu: "View the menu",
    errName: "Enter name, date and time", err: "An error occurred, please try again.",
  },
};

export default function PublicReservation() {
  const [lang, setLang] = useState("it");
  const [book, setBook] = useState(emptyBooking);
  const [slots, setSlots] = useState([]);
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(false);
  const tr = T[lang];

  const onDateChange = async (date) => {
    setBook((b) => ({ ...b, date, time: "" }));
    if (!date) return setSlots([]);
    try { const { data } = await api.get(`/public/availability?date=${date}`); setSlots(data.slots || []); }
    catch { setSlots([]); }
  };

  const submitBooking = async () => {
    if (!book.customer_name || !book.date || !book.time) return toast.error(tr.errName);
    setBooking(true);
    try {
      await api.post("/public/reservations", { ...book, guests: parseInt(book.guests) });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || tr.err); }
    finally { setBooking(false); }
  };

  const reset = () => { setBook(emptyBooking); setSlots([]); setDone(false); };

  return (
    <div className="min-h-screen bg-[#14110E] text-[#EFE9DD] flex flex-col" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="public-reservation-page">
      <div className="relative overflow-hidden">
        <img src="https://customer-assets-v7afamib.emergentagent.net/job_locale-manager-4/artifacts/watx1ybf_ChatGPT%20Image%2028%20ago%202026%2C%2008_43_00.png" alt="Dogana 2.0" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,17,14,0.6), rgba(20,17,14,0.97))" }} />
        <div className="absolute top-4 right-4 z-20 flex items-center rounded-full border border-white/20 bg-black/30 backdrop-blur-md overflow-hidden" data-testid="lang-toggle">
          {["it", "en"].map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
              className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide transition-colors ${lang === l ? "text-[#14110E]" : "text-white/70 hover:text-white"}`}
              style={lang === l ? { backgroundColor: GOLD } : {}}>
              {l === "it" ? "IT" : "EN"}
            </button>
          ))}
        </div>
        <div className="relative z-10 text-center px-6 pt-16 pb-10">
          <img src="/logo-dogana.png" alt="Dogana 2.0" className="h-14 md:h-20 w-auto mx-auto mb-4 drop-shadow-2xl" />
          <p className="tracking-[0.4em] text-xs uppercase mb-2" style={{ color: GOLD }}>{tr.eyebrow}</p>
          <h1 className="font-display text-3xl md:text-5xl font-light">{tr.title}</h1>
        </div>
      </div>

      <div className="flex-1 max-w-lg w-full mx-auto px-4 -mt-6 pb-16 relative z-10">
        <div className="rounded-2xl border border-white/10 bg-[#1c1813]/90 backdrop-blur-xl p-6 md:p-8 shadow-2xl">
          {done ? (
            <div className="text-center py-8" data-testid="booking-success">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: GOLD }} />
              <h2 className="font-display text-2xl mb-2">{tr.okTitle}</h2>
              <p className="text-white/60 mb-8">{tr.okBody}</p>
              <div className="flex flex-col gap-3">
                <Button onClick={reset} className="rounded-full h-11 text-[#14110E] border-0" style={{ backgroundColor: GOLD }} data-testid="book-another-button">{tr.another}</Button>
                <a href="/menu" className="text-sm text-white/60 hover:text-white underline underline-offset-4">{tr.menu}</a>
              </div>
            </div>
          ) : (
            <>
              <p className="text-white/60 text-sm mb-6">{tr.intro}</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>{tr.name}</Label><Input value={book.customer_name} onChange={(e) => setBook({ ...book, customer_name: e.target.value })} data-testid="book-name-input" /></div>
                  <div className="space-y-2"><Label>{tr.phone}</Label><Input value={book.phone} onChange={(e) => setBook({ ...book, phone: e.target.value })} data-testid="book-phone-input" /></div>
                </div>
                <div className="space-y-2"><Label>{tr.email}</Label><Input type="email" value={book.customer_email} onChange={(e) => setBook({ ...book, customer_email: e.target.value })} placeholder={tr.emailPh} data-testid="book-email-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>{tr.date}</Label><Input type="date" value={book.date} onChange={(e) => onDateChange(e.target.value)} data-testid="book-date-input" /></div>
                  <div className="space-y-2"><Label>{tr.guests}</Label><Input type="number" min="1" value={book.guests} onChange={(e) => setBook({ ...book, guests: e.target.value })} data-testid="book-guests-input" /></div>
                </div>
                {book.date && (
                  <div className="space-y-2">
                    <Label>{tr.slot}</Label>
                    {slots.length === 0 ? <p className="text-sm text-white/50">{tr.noSlot}</p> : (
                      <div className="flex flex-wrap gap-2" data-testid="slot-list">
                        {slots.map((s) => {
                          const full = s.remaining < parseInt(book.guests || 1);
                          return (
                            <button key={s.time} type="button" disabled={full} onClick={() => setBook((b) => ({ ...b, time: s.time }))} data-testid={`slot-${s.time}`}
                              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${book.time === s.time ? "text-[#14110E] border-transparent" : full ? "opacity-40 cursor-not-allowed border-white/15" : "border-white/20 hover:border-white/50"}`}
                              style={book.time === s.time ? { backgroundColor: GOLD } : {}}>
                              {s.time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-2"><Label>{tr.notes}</Label><Textarea value={book.notes} onChange={(e) => setBook({ ...book, notes: e.target.value })} placeholder={tr.notesPh} data-testid="book-notes-input" /></div>
                <Button onClick={submitBooking} disabled={booking} className="rounded-full w-full h-11 text-[#14110E] border-0 hover:brightness-110" style={{ backgroundColor: GOLD }} data-testid="book-submit-button">
                  {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarCheck className="w-4 h-4 mr-2" /> {tr.confirm}</>}
                </Button>
              </div>
            </>
          )}
        </div>
        <p className="text-white/40 text-sm flex items-center justify-center gap-2 mt-8"><MapPin className="w-3.5 h-3.5" /> Dogana 2.0</p>
      </div>
    </div>
  );
}
